import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, TextField } from "@pack-it-up/ui-web";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { AuthCard, AuthError } from "./AuthCard.js";
import { ProviderButtons } from "./ProviderButtons.js";
import { signInSchema, isValid } from "./schemas.js";

export const SignIn = (): ReactNode => {
  const { auth } = useStores();
  const error = useStoreValue(auth, (s) => s.error);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const valid = isValid(signInSchema, { email, password });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    // No imperative navigate on success — the auth-stream gate flip routes the app
    // (see SignUp.tsx for the stale-navigate race this avoids).
    await auth.getState().signIn(email, password);
    setSubmitting(false);
  };

  return (
    <AuthCard
      title="Sign in"
      footer={
        <>
          <Link to="/forgot">Forgot password?</Link>
          <span>
            New here? <Link to="/signup">Create an account</Link>
          </span>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }} noValidate>
        <TextField label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} />
        <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} />
        {error !== null && <AuthError message={error} />}
        <Button type="submit" disabled={!valid || submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
        <ProviderButtons />
      </form>
    </AuthCard>
  );
};
